<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FileMenu from './FileMenu.vue';
import InvitationFields from './InvitationFields.vue';
import AddPanel from '../AddPanel.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';
import { fmtDate, sortByDate, periodDescriptor } from '../../domain/format.js';
import { INVITE_TYPES } from '../../domain/invitations.js';

const props = defineProps({
  player: { type: Object, required: true },
});

const { t } = useI18n();
const { can } = useAuth();
const { confirm } = useConfirm();
const { upload, remove, busy } = useFileActions();

const fileInput = ref(null);
const uploadTarget = ref(null);

const canEdit = computed(() => can('travel', 'write'));

const entries = computed(() =>
  sortByDate(props.player.invitations || [], 'issuedDate').map((inv) => ({
    inv,
    idx: props.player.invitations.indexOf(inv),
  }))
);

function typeLabel(type) {
  return INVITE_TYPES.includes(type) ? t(`documents.inviteType.${type}`) : type;
}

/** Zeitraum als übersetzten Text — die Fachlogik liefert nur Schlüssel. */
function periodText(inv) {
  const d = periodDescriptor(inv);
  return t(d.key, d.params || {});
}

/* periodMode 'dates' und 'open' sind hier bewusst so benannt, wie sie es im
   Vorgänger-Formular (src/_legacy/app.js, invite-period-mode) schon waren.
   periodDescriptor() (domain/format.js) prüft nur explizit auf 'tba' und
   'open' — 'dates' fällt dort in den allgemeinen from/until-Zweig, ist aber
   kein Zufallswert, sondern der historisch verwendete. */
function periodModeFor(from, until) {
  if (from && until) return 'dates';
  if (from) return 'open';
  return 'tba';
}

const emptyDraft = () => ({ type: 'nationalCamp', label: '', from: '', until: '' });
const draft = reactive(emptyDraft());
const draftValid = computed(() => !!draft.label.trim());

function resetDraft() {
  Object.assign(draft, emptyDraft());
}

function add({ close }) {
  if (!draftValid.value) return;
  props.player.invitations.push({
    type: draft.type,
    label: draft.label.trim(),
    periodMode: periodModeFor(draft.from, draft.until),
    from: draft.from, until: draft.until, issuedDate: '', note: '', file: null,
  });
  resetDraft();
  close();
}

/* periodMode hängt beim Anlegen wie beim Bearbeiten von from/until ab — beim
   Verlassen des Bearbeiten-Modus neu berechnen, sonst bliebe der alte Modus
   stehen, obwohl sich die Daten geändert haben. */
function saveEdit(inv) {
  inv.label = inv.label.trim();
  inv.periodMode = periodModeFor(inv.from, inv.until);
  inv._edit = false;
}

async function removeInvite(idx) {
  const inv = props.player.invitations[idx];
  if (!inv) return;

  const ok = await confirm({
    title: t('confirm.deleteInviteTitle'),
    message: t('confirm.deleteInvite', { name: inv.label || '—' }),
    detail: inv.file ? t('confirm.deleteInviteFile') : '',
  });
  if (!ok) return;

  props.player.invitations.splice(idx, 1);
  if (inv.file) remove(inv.file);
}

/* Verweis UND Datei entfernen. Nur den Verweis zu löschen würde die Datei
   verwaist im Speicher zurücklassen — sie wäre bezahlt, aber unauffindbar.
   Die Rückfrage stellt bereits das Dateimenü. */
function removeInviteFile(inv) {
  const gone = inv.file;
  inv.file = null;
  if (gone) remove(gone);
}

function pickFile(idx) {
  uploadTarget.value = idx;
  fileInput.value.click();
}

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  const idx = uploadTarget.value;
  uploadTarget.value = null;
  if (!file || idx === null) return;

  const stored = await upload(file, `invitations/${props.player.id}`);
  const previous = props.player.invitations[idx].file;
  props.player.invitations[idx].file = stored;
  if (previous) await remove(previous);
}
</script>

<template>
  <div class="doc-group">
    <h3>{{ $t('documents.invitations') }}</h3>

    <div v-if="!entries.length" class="feed-empty">{{ $t('documents.noInvitations') }}</div>

    <div v-for="{ inv, idx } in entries" :key="idx" class="uploaded-doc stacked">
      <!-- Bearbeiten -->
      <div v-if="inv._edit && canEdit" class="entry-edit">
        <InvitationFields :invitation="inv" />
        <div class="er-actions">
          <button class="btn ghost small" @click="saveEdit(inv)">{{ $t('common.save') }}</button>
          <button class="icon-btn danger" @click="removeInvite(idx)">{{ $t('common.delete') }}</button>
        </div>
      </div>

      <!-- Ansicht -->
      <div v-else class="invite-view">
        <span class="type-badge">{{ typeLabel(inv.type) }}</span>
        <div class="ud-body">
          <!-- Staff (F7) bekommt die freie Bezeichnung nicht: nur Art (Plakette) und Termine -->
          <div v-if="inv.label" class="ud-label">{{ inv.label }}</div>
          <div class="ud-sub">{{ periodText(inv) }}</div>
          <div v-if="inv.issuedDate" class="ud-sub">
            {{ $t('documents.issuedOn') }}: {{ fmtDate(inv.issuedDate) }}
          </div>
          <div v-if="inv.note" class="ud-note">{{ inv.note }}</div>
          <FileMenu
            v-if="inv.file"
            :file="inv.file"
            :name="inv.label"
            area="travel"
            @remove="removeInviteFile(inv)"
          />
        </div>
        <div v-if="canEdit" class="entry-actions">
          <button class="file-label" :disabled="busy" @click="pickFile(idx)">
            {{ busy ? $t('common.loading') : inv.file ? $t('files.replace') : $t('documents.addLetter') }}
          </button>
          <button class="icon-btn" @click="inv._edit = true">{{ $t('common.edit') }}</button>
          <button class="icon-btn" @click="removeInvite(idx)">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

    <AddPanel
      v-if="canEdit"
      :label="$t('documents.addInvitation')"
      :invalid="!draftValid"
      @submit="add"
      @cancel="resetDraft"
    >
      <InvitationFields :invitation="draft" />
    </AddPanel>

    <input v-if="canEdit" ref="fileInput" type="file" accept="application/pdf,image/*" hidden @change="onPick" />
  </div>
</template>

<style scoped>
.uploaded-doc.stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.invite-view {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.entry-edit {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.er-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.entry-actions {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}
.file-label {
  background: none;
  font: inherit;
}
.icon-btn.danger {
  color: var(--red);
}
</style>
